import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Crop, RotateCcw, X } from 'lucide-react';
import {
  cropDocumentWithCorners,
  defaultDocumentCorners,
  detectDocumentCornersOnly,
  fullDocumentCorners,
  loadOrientedImage,
  orderDocumentCorners,
  type ScanPoint,
} from '../../lib/documentScan';

const CORNER_LABELS = ['Top left', 'Top right', 'Bottom right', 'Bottom left'] as const;

interface VaultManualCropEditorProps {
  sourceBlob: Blob;
  onApply: (blob: Blob) => void;
  onCancel: () => void;
}

interface ImageLayout {
  naturalWidth: number;
  naturalHeight: number;
  displayWidth: number;
  displayHeight: number;
  offsetX: number;
  offsetY: number;
  scale: number;
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function toDisplay(point: ScanPoint, layout: ImageLayout): ScanPoint {
  return {
    x: layout.offsetX + point.x * layout.scale,
    y: layout.offsetY + point.y * layout.scale,
  };
}

export function VaultManualCropEditor({ sourceBlob, onApply, onCancel }: VaultManualCropEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [corners, setCorners] = useState<ScanPoint[]>([]);
  const [layout, setLayout] = useState<ImageLayout | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const measureLayout = useCallback((img: HTMLImageElement) => {
    const container = containerRef.current;
    if (!container) return;
    const bounds = container.getBoundingClientRect();
    const scale = Math.min(bounds.width / img.naturalWidth, bounds.height / img.naturalHeight);
    const displayWidth = img.naturalWidth * scale;
    const displayHeight = img.naturalHeight * scale;
    setLayout({
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight,
      displayWidth,
      displayHeight,
      offsetX: (bounds.width - displayWidth) / 2,
      offsetY: (bounds.height - displayHeight) / 2,
      scale,
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const img = await loadOrientedImage(sourceBlob);
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas not supported');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);

        const detected = await detectDocumentCornersOnly(sourceBlob);
        if (cancelled) return;

        setImageUrl(canvas.toDataURL('image/jpeg', 0.95));
        setCorners(orderDocumentCorners(
          detected?.corners ?? defaultDocumentCorners(img.naturalWidth, img.naturalHeight),
        ));
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load image for manual crop.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sourceBlob]);

  useEffect(() => {
    if (!imageUrl) return undefined;
    const img = new Image();
    img.onload = () => measureLayout(img);
    img.src = imageUrl;

    const onResize = () => measureLayout(img);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [imageUrl, measureLayout]);

  const pointerToImage = useCallback((clientX: number, clientY: number): ScanPoint | null => {
    if (!layout || !containerRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    const x = (clientX - rect.left - layout.offsetX) / layout.scale;
    const y = (clientY - rect.top - layout.offsetY) / layout.scale;
    return {
      x: clamp(x, 0, layout.naturalWidth),
      y: clamp(y, 0, layout.naturalHeight),
    };
  }, [layout]);

  const handlePointerDown = (index: number) => (event: React.PointerEvent) => {
    event.preventDefault();
    setDragIndex(index);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  useEffect(() => {
    if (dragIndex === null) return undefined;

    const onMove = (event: PointerEvent) => {
      const next = pointerToImage(event.clientX, event.clientY);
      if (!next) return;
      setCorners((prev) => {
        const copy = [...prev];
        copy[dragIndex] = next;
        return orderDocumentCorners(copy);
      });
    };

    const onUp = () => setDragIndex(null);

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [dragIndex, pointerToImage]);

  const resetCorners = () => {
    setError(null);
    if (layout) {
      setCorners(orderDocumentCorners(fullDocumentCorners(layout.naturalWidth, layout.naturalHeight)));
      return;
    }
    void loadOrientedImage(sourceBlob)
      .then((img) => {
        setCorners(orderDocumentCorners(fullDocumentCorners(img.naturalWidth, img.naturalHeight)));
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Could not reset crop area.');
      });
  };

  const applyCrop = async () => {
    setApplying(true);
    setError(null);
    try {
      const ordered = orderDocumentCorners(corners);
      const blob = await cropDocumentWithCorners(sourceBlob, ordered, { enhance: true });
      onApply(blob);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not apply manual crop.');
    } finally {
      setApplying(false);
    }
  };

  const displayCorners = layout ? corners.map((point) => toDisplay(point, layout)) : [];
  const polygonPoints = displayCorners.map((p) => `${p.x},${p.y}`).join(' ');

  return (
    <div className="wfx-manual-crop-overlay" role="dialog" aria-modal="true" aria-label="Manual document crop">
      <header className="wfx-manual-crop-head">
        <div>
          <h3>Adjust crop</h3>
          <p className="wfx-muted">Drag the corners to match the document edges</p>
        </div>
        <button type="button" className="ghost-icon-button" onClick={onCancel} aria-label="Close manual crop">
          <X size={20} />
        </button>
      </header>

      {error && <div className="auth-error" role="alert">{error}</div>}

      <div
        ref={containerRef}
        className="wfx-manual-crop-stage"
      >
        {loading && <p className="wfx-muted wfx-manual-crop-loading">Loading image…</p>}
        {!loading && imageUrl && layout && (
          <>
            <img
              src={imageUrl}
              alt="Document to crop"
              className="wfx-manual-crop-image"
              draggable={false}
            />
            <svg className="wfx-manual-crop-svg" aria-hidden="true">
              <defs>
                <mask id="wfx-manual-crop-mask">
                  <rect x="0" y="0" width="100%" height="100%" fill="white" />
                  {polygonPoints && <polygon points={polygonPoints} fill="black" />}
                </mask>
              </defs>
              <rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                fill="rgba(15, 23, 42, 0.55)"
                mask="url(#wfx-manual-crop-mask)"
              />
              {polygonPoints && (
                <polygon
                  points={polygonPoints}
                  fill="rgba(255, 107, 0, 0.08)"
                  stroke="var(--color-primary)"
                  strokeWidth="2"
                />
              )}
            </svg>
            {displayCorners.map((point, index) => (
              <button
                key={CORNER_LABELS[index]}
                type="button"
                className={`wfx-manual-crop-handle${dragIndex === index ? ' is-dragging' : ''}`}
                style={{ left: `${point.x}px`, top: `${point.y}px` }}
                aria-label={CORNER_LABELS[index]}
                onPointerDown={handlePointerDown(index)}
              />
            ))}
          </>
        )}
      </div>

      <div className="wfx-manual-crop-actions">
        <button type="button" className="secondary-action" onClick={resetCorners} disabled={applying}>
          <RotateCcw size={14} /> Reset to full page
        </button>
        <div className="wfx-manual-crop-actions-right">
          <button type="button" className="secondary-action" onClick={onCancel} disabled={applying}>
            Cancel
          </button>
          <button type="button" className="primary-action" onClick={() => void applyCrop()} disabled={applying || loading}>
            <Crop size={14} /> {applying ? 'Applying…' : 'Apply crop'}
          </button>
        </div>
      </div>
    </div>
  );
}
